import {inject} from '@angular/core';
import {CanActivateFn, Router, UrlTree} from '@angular/router';
import {of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {PermissionService} from '../permission/permission.service';
import {CatalogService} from '../catalog.service';
import {serverErrorMessage} from '../../components/my-checks/shared/server-error-message';

/**
 * Schützt den plattform-globalen Admin-Bereich – globale Regelwerke (`admin/rulesets`) und
 * geteilte myChecks (`admin/my-checks`). Zugriff nur mit dem plattform-globalen Privileg
 * GLOBAL_CHECKRULE_ADMIN (per DB-Eintrag vergeben).
 */
export function rulesetAdminGuard(): CanActivateFn {
  return () => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);
    if (permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) {
      return true;
    }
    router.navigate(['organisations']);
    return false;
  };
}

/**
 * Sperrt die Regelset-Editoren des Admin-Bereichs (`admin/my-checks/:id/ki|ids|hybrid`) in einer
 * Stage ohne Katalog-Einreichen (Live). Die Editoren haben im Admin-Modus keinen Nur-lesen-Zustand;
 * das Backend lehnt Schreibzugriffe dort ohnehin mit 403 ab. Deshalb zurück zur Übersicht.
 *
 * <p>Schlägt das Laden der Katalog-Konfiguration fehl, bleibt der Editor ebenfalls zu – ohne sie
 * wissen wir nicht, ob Schreiben erlaubt ist. Der Fehler wird gemeldet.
 */
export function catalogSubmissionGuard(): CanActivateFn {
  return () => {
    const catalogService = inject(CatalogService);
    const router = inject(Router);
    const toastService = inject(BicToastService);
    const overview = router.createUrlTree(['admin', 'my-checks']);
    return catalogService.getConfig().pipe(
      map((config): boolean | UrlTree => {
        if (config.submissionAllowed) return true;
        toastService.info('In Live nur lesend – Änderungen erfolgen in Beta');
        return overview;
      }),
      catchError(err => {
        toastService.error(serverErrorMessage(err, 'Katalog-Konfiguration konnte nicht geladen werden.'));
        return of(overview);
      }),
    );
  };
}
